import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { makeStyles } from '@mui/styles';
import helpers from 'src/styles/helpers';
import sections from 'src/styles/sections';
import { palette } from '@livable/saas-react-components';
const useStyles = makeStyles(_objectSpread(_objectSpread(_objectSpread({}, helpers), sections), {}, {
  // The page grows with its content and the app's page container scrolls it, so the banner and the tiles
  // scroll away and leave the screen to the table (LB-6026). Pinning it to the window's height gave the
  // table only what they left: about four rows on a laptop.
  pageWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 0 auto',
    paddingTop: '12px',
    backgroundColor: palette.shades[100],
    borderTop: "1px solid ".concat(palette.shades[300])
  },
  pageHeader: {
    flexShrink: 0,
    padding: '4px 16px 12px'
  },
  headerWrapper: {
    flexShrink: 0,
    borderBottom: "1px solid ".concat(palette.shades[300])
  },
  headerTabs: {
    padding: '0 12px'
    // borderBottom: `1px solid ${palette.shades[300]}`,
  },
  headerFilters: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '65px'
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    // Inset the banner and the dashboard from the page edges so neither runs full-bleed (LB-5820).
    padding: '8px 16px 16px'
  },
  // Bills tab, framed as a contained panel so the dashboard reads as a distinct, prominent section
  // rather than loose full-bleed content (LB-5820). As tall as its content (LB-6026).
  billsRoot: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
    border: "1px solid ".concat(palette.shades[300]),
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)'
  },
  '@keyframes billsRefreshSpin': {
    from: {
      transform: 'rotate(0deg)'
    },
    to: {
      transform: 'rotate(360deg)'
    }
  },
  /**
   * Refresh sits beside Filters in the left group and is drawn as a primary-coloured button rather
   * than a quiet icon, so the reload affordance is easy to find. Fills solid on hover (LB-5820).
   */
  billsRefreshButton: {
    '&.MuiButton-root': {
      flexShrink: 0,
      alignSelf: 'stretch',
      minHeight: 36,
      padding: '0 12px',
      fontSize: 13,
      fontWeight: 600,
      lineHeight: '16px',
      color: palette.primary.main,
      backgroundColor: palette.shades[100],
      border: "1px solid ".concat(palette.primary.main),
      boxShadow: 'none',
      '& .MuiButton-startIcon': {
        marginLeft: 0,
        marginRight: 8
      },
      '& .MuiSvgIcon-root': {
        width: 20,
        height: 20
      },
      '&:hover': {
        backgroundColor: palette.primary.main,
        color: palette.common.white,
        borderColor: palette.primary.main,
        boxShadow: 'none'
      }
    },
    // Beat the toolbar's white-icon rule (meant for the contained buttons) so the reload glyph reads
    // in the primary colour; it turns white on hover once the background fills.
    '&&& path': {
      fill: palette.primary.main
    },
    '&&&:hover path': {
      fill: palette.common.white
    },
    '&.Mui-disabled': {
      opacity: 0.5
    }
  },
  billsRefreshButtonSpinning: {
    '& svg': {
      animation: '$billsRefreshSpin 0.8s linear infinite'
    }
  },
  /**
   * Both groups wrap, so on a narrow window the controls move to the next line instead of running
   * under the buttons beside them (LB-5926).
   */
  billsToolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    padding: '16px',
    flexShrink: 0,
    borderBottom: "1px solid ".concat(palette.shades[300]),
    /**
     * One height for everything on the strip. The search field, the three selects and the buttons
     * each have their own natural height, and left alone they sit at four slightly different sizes
     * along the same line.
     */
    '& .MuiButton-root': {
      height: 40
    },
    '& .MuiOutlinedInput-root': {
      height: 40
    },
    '& .MuiButton-root path': {
      fill: palette.shades[100]
    },
    '& > div:first-of-type > div > div:first-of-type': {
      top: 5,
      left: 8
    },
    '& .MuiChip-root': {
      padding: '8px',
      '& svg': {
        marginRight: '4px'
      }
    }
  },
  /**
   * Sized to its controls on one line, so the right-hand buttons move to a line of their own as soon
   * as this group cannot fit on one, and the filters never wrap under them.
   */
  billsToolbarLeft: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 12,
    flex: '1 1 auto',
    minWidth: 0,
    /**
     * The search field, through the wrapper its icon sits in: wide enough for its placeholder to name
     * what it searches (LB-6036), shrinking to its line in a phone-width window. A width rather than a
     * flex basis, because the group above is sized from its controls' widths and a basis is not one of them.
     */
    '& > div:first-of-type': {
      width: 560,
      flex: '0 1 auto',
      minWidth: 0
    }
  },
  /**
   * The status pill, drawn the same way as the one in the upload modal so a bill looks the same
   * wherever its state is shown. Dot in the saturated shade, label in the darker one.
   */
  billsStatusChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '3px 10px',
    borderRadius: 4,
    fontSize: 12,
    lineHeight: '18px',
    fontWeight: 600,
    whiteSpace: 'nowrap'
  },
  billsStatusDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    flexShrink: 0
  },
  billsStatus_neutral: {
    backgroundColor: palette.shades[200],
    color: palette.shades[800],
    '& $billsStatusDot': {
      backgroundColor: palette.shades[600]
    }
  },
  billsStatus_info: {
    backgroundColor: palette.info.light,
    color: palette.info.dark,
    '& $billsStatusDot': {
      backgroundColor: palette.info.main
    }
  },
  billsStatus_success: {
    backgroundColor: palette.success.light,
    color: palette.success.dark,
    '& $billsStatusDot': {
      backgroundColor: palette.success.main
    }
  },
  billsStatus_warning: {
    backgroundColor: palette.warning.light,
    color: palette.warning.dark,
    '& $billsStatusDot': {
      backgroundColor: palette.warning.main
    }
  },
  billsStatus_error: {
    backgroundColor: palette.error.light,
    color: palette.error.dark,
    '& $billsStatusDot': {
      backgroundColor: palette.error.main
    }
  },
  /** The prompt on a bill nobody has placed. Reads as an invitation rather than as missing data. */
  billsUnmatchedProperty: {
    color: palette.primary.main
  },
  /** An address is the sender, not a label, so it is set apart from the channel names beside it. */
  billsSenderEmail: {
    color: palette.primary.main
  },
  billsToolbarRight: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 8,
    marginLeft: 'auto'
  },
  billsTabs: {
    marginBottom: 16,
    // Same inset as the cards and the toolbar, so the whole left edge of the page reads as one line.
    padding: '0 16px',
    minHeight: 40,
    '& .MuiTab-root': {
      minHeight: 40,
      textTransform: 'none'
    }
  },
  billsTabLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8
  },
  /**
   * The count rides beside the name rather than in brackets after it, so the number reads as a
   * quantity of work and not as part of the tab's title.
   */
  billsTabCount: {
    minWidth: 20,
    padding: '0 6px',
    borderRadius: 10,
    fontSize: 11,
    lineHeight: '18px',
    backgroundColor: palette.shades[200],
    color: palette.shades[800]
  },
  billsTabCountActive: {
    backgroundColor: palette.primary.main,
    color: palette.common.white
  },
  billsTablePaper: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: 'none',
    border: 'none',
    borderRadius: 0,
    '& .MuiTableCell-root': {
      fontSize: 14,
      verticalAlign: 'top'
    },
    '& .MuiTableRow-root .MuiTableCell-root:first-of-type': {
      paddingLeft: 4,
      paddingRight: 4
    },
    '& .MuiTableHead-root .MuiTableCell-root:first-of-type': {
      paddingTop: 8
    },
    '& .MuiTableBody-root .MuiTableCell-root:first-of-type': {
      paddingTop: 10
    }
  },
  /**
   * As tall as its rows, so the page is the one thing that scrolls up and down (LB-6026): a box of its
   * own inside the page caught the wheel wherever the pointer sat over the table and moved the rows in a
   * window a few rows high. It still scrolls sideways, since the table is wider than most windows.
   */
  billsTableContainer: {
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch'
  },
  billsTable: {
    minWidth: 2040
  },
  billsFileNameCell: {
    minWidth: 200,
    maxWidth: 260
  },
  billsFileNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    overflow: 'hidden'
  },
  /** th/td: force single-line cell content (Typography noWrap alone is not enough in tables) */
  billsCellNoWrap: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    wordBreak: 'normal',
    overflowWrap: 'normal',
    verticalAlign: 'middle'
  },
  /**
   * The theme gives every cell 5px a side, so the narrow columns from Utilities to Invoice # ran into each other and a
   * bill's fourth utility icon touched its account number (LB-6036). `&&` outranks the theme's own cell class, which
   * MUI injects after this sheet.
   */
  billsSpacedCell: {
    '&&': {
      paddingRight: 16
    }
  },
  billsUtilitiesCell: {
    whiteSpace: 'nowrap'
  },
  /** Room for four icons (water, sewer, trash, electricity is the most a bill has shown), so the column holds still. */
  billsUtilitiesIcons: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexWrap: 'nowrap',
    minWidth: 92,
    '& svg': {
      display: 'block',
      flexShrink: 0,
      width: 20,
      height: 20
    }
  },
  /** One icon, named on hover on its own rather than the whole set at once. */
  billsUtilityIcon: {
    display: 'inline-flex',
    flexShrink: 0
  },
  /** Account numbers line up digit for digit, and a long one ends in an ellipsis instead of widening the table. */
  billsAccountCell: {
    maxWidth: 180,
    fontVariantNumeric: 'tabular-nums'
  },
  billsPropertiesCell: {
    minWidth: 220,
    maxWidth: 320
  },
  billsTableChip: {
    fontSize: 13,
    maxWidth: '100%',
    '& .MuiChip-label': {
      fontSize: 13,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }
  },
  billsStickyCheckboxHead: {
    position: 'sticky',
    left: 0,
    zIndex: 5,
    backgroundColor: '#FFFFFF',
    boxShadow: 'none',
    borderRight: "1px solid ".concat(palette.shades[300])
  },
  billsStickyCheckbox: {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    backgroundColor: '#FFFFFF',
    boxShadow: 'none',
    borderRight: "1px solid ".concat(palette.shades[300])
  },
  billsStickyActionsHead: {
    position: 'sticky',
    right: 0,
    zIndex: 5,
    backgroundColor: '#FFFFFF',
    boxShadow: 'none',
    borderLeft: "1px solid ".concat(palette.shades[300])
  },
  billsStickyActions: {
    position: 'sticky',
    right: 0,
    zIndex: 1,
    backgroundColor: '#FFFFFF',
    boxShadow: 'none',
    borderLeft: "1px solid ".concat(palette.shades[300])
  },
  /**
   * The empty message is a row in the table now rather than a panel shown instead of it, so the
   * pagination control below stays mounted and a page with nothing on it can still be paged off.
   */
  billsTableEmptyCell: {
    // Left, so the message below starts at the table's left edge and its sticky offset carries it from there.
    textAlign: 'left',
    padding: '48px 16px',
    borderBottom: 'none'
  },
  /**
   * Centred in the part of the table you can see, not in the table. The cell spans every column, some 2,400px of a
   * table that scrolls sideways, so centring the text in it put the message far off to the right. Sticky at half the
   * scroll area's width, then pulled back by half its own, it stays in the middle wherever the table is scrolled.
   */
  billsTableEmptyMessage: {
    display: 'inline-block',
    position: 'sticky',
    left: '50%',
    transform: 'translateX(-50%)',
    maxWidth: 'min(640px, 90vw)',
    textAlign: 'center'
  },
  tableHeadCell: {
    fontWeight: 600,
    backgroundColor: '#FFFFFF',
    borderBottom: "1px solid ".concat(palette.shades[300]),
    whiteSpace: 'nowrap'
  },
  tableBodyRow: {
    '& .MuiTableCell-root': {
      borderBottom: "1px solid ".concat(palette.shades[300])
    },
    /**
     * The last row keeps its rule too. Without this the table stops on an open edge and the final
     * bill looks like it is still being written; the higher-specificity selector is there to beat
     * whatever strips it by default.
     */
    '&:last-child .MuiTableCell-root': {
      borderBottom: "1px solid ".concat(palette.shades[300])
    }
  },
  billsClickableRow: {
    cursor: 'pointer'
  },
  pricingsCell: {
    verticalAlign: 'top',
    paddingTop: 8,
    paddingBottom: 8
  },
  pricingsStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    paddingRight: '24px'
  },
  pricingCard: {
    border: "1px solid ".concat(palette.shades[300]),
    borderRadius: 2,
    padding: '8px 10px',
    backgroundColor: '#f8f9fa'
  },
  pricingCardContent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8
  },
  pricingAmount: {
    whiteSpace: 'nowrap',
    marginLeft: 'auto'
  },
  actionsCell: {
    verticalAlign: 'top',
    paddingTop: 8,
    paddingBottom: 8,
    minWidth: 68,
    width: 68,
    whiteSpace: 'nowrap'
  },
  /**
   * One strip along the bottom of the table: what is selected and what can be done with it on the
   * left, paging on the right. The border lives here rather than on the pagination so the rule runs
   * the full width whether or not anything is selected.
   */
  billsFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    flexShrink: 0,
    minHeight: 52,
    paddingLeft: 8,
    borderTop: "1px solid ".concat(palette.shades[300]),
    // Three variable-width things share this strip once the list is partial, and every ancestor of
    // it clips rather than scrolls. Without wrapping, a narrow viewport pushes the paging controls
    // off the right-hand edge -- back under the launcher the padding below exists to clear, and past
    // about 1080px clean off the panel. Wrapping drops them to their own line instead.
    flexWrap: 'wrap',
    rowGap: 8
  },
  billsFooterActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
    flexWrap: 'wrap'
  },
  /** The count is the part that changes, so it carries the weight and the words around it do not. */
  billsFooterCount: {
    fontWeight: 600
  },
  billsFooterSeparator: {
    color: palette.shades[500]
  },
  /** Points up: the Download menu opens above the footer it sits in (LB-6033). */
  billsDownloadChevron: {
    display: 'inline-flex',
    transform: 'rotate(180deg)'
  },
  /** What the download will act on, said at the top of its menu. */
  billsDownloadScope: {
    fontSize: '12px',
    lineHeight: '32px',
    color: palette.shades[600]
  },
  /**
   * Clears the launchers fixed to the bottom-right corner of the viewport, HubSpot's chat and Livy's button, which
   * sit over the page arrows otherwise — so the control that moves you through the list is the one thing you cannot
   * click (LB-6036).
   *
   * The padding goes on the inner toolbar, not the root: this file styles through JSS and MUI v5
   * styles its own root through emotion, which wins. Reaching the toolbar by its own class clears
   * that, and is why the first attempt at this changed nothing on screen.
   */
  tablePagination: {
    flexShrink: 0,
    // Keeps the paging controls against the right edge on a line of their own: a wrapped line holds
    // only this, and space-between packs a single item at the start.
    marginLeft: 'auto',
    '& .MuiTablePagination-toolbar': {
      paddingRight: 144
    }
  },
  /**
   * Sits with the pagination, and clears the same launcher.
   *
   * Deliberately NOT shrinkable: shrinking is inert once the row wraps (a formed flex line never
   * needs it), and allowing it without the wrap would let the text spill out of its own box across
   * the paging controls -- trading a clipped strip for an overlapping one.
   */
  partialLoadNotice: {
    flexShrink: 0,
    marginLeft: 'auto',
    paddingRight: 16,
    color: palette.shades[700]
  },
  // Pricings tab: select all bar and scroll area
  pricingsSelectAllBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 10px',
    borderBottom: "1px solid ".concat(palette.shades[300]),
    backgroundColor: '#fff',
    flexShrink: 0
  },
  pricingsSelectAllCheckbox: {
    padding: 4
  },
  pricingsSelectAllLabel: {
    userSelect: 'none'
  },
  pricingsScrollWrapper: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto'
  },
  pricingsEmpty: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  pricingsRecord: {
    borderBottom: "1px solid ".concat(palette.shades[300]),
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  pricingsRecordHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 0,
    backgroundColor: '#f3f7fa',
    borderBottom: "1px solid ".concat(palette.shades[300]),
    padding: '12px 16px',
    fontWeight: 600,
    fontSize: 15,
    justifyContent: 'space-between'
  },
  pricingsRecordHeaderLeft: {
    display: 'flex',
    alignItems: 'center'
  },
  pricingsRecordHeaderRight: {
    display: 'flex',
    alignItems: 'center'
  },
  pricingsRecordHeaderExpand: {
    padding: 4,
    marginRight: 4
  },
  pricingsRecordHeaderTitle: {
    marginRight: 8
  },
  pricingsRecordHeaderDivider: {
    width: 1,
    height: 20,
    backgroundColor: palette.shades[300],
    margin: '0 12px',
    flexShrink: 0
  },
  pricingsRecordHeaderCount: {
    fontWeight: 500,
    fontSize: 14,
    color: palette.shades[700]
  },
  pricingsRecordHeaderAdd: {
    marginLeft: 0,
    padding: 4
  },
  pricingsRecordContent: {
    padding: 0,
    backgroundColor: '#FFFFFF'
  },
  pricingsContentRow: {
    display: 'flex',
    alignItems: 'stretch',
    padding: '4px 10px',
    borderBottom: "1px solid ".concat(palette.shades[300]),
    gap: 12,
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  pricingsContentRowCheckbox: {
    padding: 4,
    flexShrink: 0,
    alignSelf: 'center'
  },
  pricingsContentRowName: {
    minWidth: 0,
    alignSelf: 'center'
  },
  pricingsContentRowChip: {
    flexShrink: 0,
    alignSelf: 'center',
    fontSize: 11,
    height: 20,
    '& .MuiChip-label': {
      fontSize: 12
    }
  },
  pricingsContentRowSubs: {
    flexShrink: 0,
    alignSelf: 'center'
  },
  pricingsContentRowDivider: {
    width: 1,
    height: 20,
    backgroundColor: palette.shades[300],
    margin: '0 2px',
    flexShrink: 0,
    alignSelf: 'center'
  },
  pricingsContentRowSpacer: {
    flex: '1 1 auto',
    minWidth: 0
  },
  pricingsContentRowPrices: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 0,
    flexShrink: 0,
    marginRight: 8,
    alignSelf: 'stretch'
  },
  pricingsPriceBlock: {
    minWidth: 70,
    marginLeft: -1,
    padding: '8px 10px',
    backgroundColor: 'transparent',
    border: 'none',
    borderLeft: "1px solid ".concat(palette.shades[300]),
    borderRight: "1px solid ".concat(palette.shades[300]),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 4
  },
  pricingsPriceBlockTitle: {
    fontSize: 12,
    lineHeight: 1.2
  },
  pricingsPriceBlockAmount: {
    fontSize: 14,
    lineHeight: 1.2
  },
  pricingsContentRowActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    flexShrink: 0,
    alignSelf: 'center',
    '& .MuiIconButton-root': {
      padding: 4
    },
    '& svg': {
      padding: 2
    }
  },
  pricingsDeleteButtonDefault: {
    opacity: 0.5
  },
  bulkDeleteConfirmBody: {
    padding: 24
  },
  bulkDeleteConfirmTitle: {
    marginBottom: 8
  }
}));
export default useStyles;